import { useMemo, useState } from "react";
import { FaQuoteLeft, FaStar, FaRegStar } from "react-icons/fa";
import { SiFiverr } from "react-icons/si";
import SectionHeading from "../../components/SectionHeading";
import Reveal from "../../components/Reveal";

// Real client reviews from Fiverr — kept verbatim.
const reviews = [
  {
    name: "claire1500",
    rating: 5,
    review:
      "I had the pleasure of working with Shahed on multiple software development projects, and I am beyond impressed with the results. Shahed demonstrated exceptional technical skills, delivering high-quality code and innovative solutions that exceeded my expectations. Communication was seamless throughout the project, and Shahed was always available to provide support and answer questions. The project was completed on time, within budget, and with great attention to detail. I highly recommend Shahed to anyone seeking reliable and professional software development services.",
  },
  {
    name: "anshulkadyan",
    rating: 5,
    review:
      "Shahed Talukder was an absolute gem in the DevOps Engineering domain! His professionalism, attention to detail, and ability to exceed expectations were unmatched. The way he surpassed the required standards and his deep understanding of the project totally blew me away. Shahed's timely delivery and outstanding communication made the whole experience fantastic. Highly recommend for anyone looking for top-notch results!",
  },
  {
    name: "iyamirei",
    rating: 5,
    review:
      "Amazing, fast service and really helpful, made business with him really easy. The support was really responsive and reliable. Explained to me in detail what I have to do in order to make any changes in the future and left with no questions unanswered. I'm very grateful for the help and recommend him to anyone who needs to deploy their app. 5/5",
  },
  {
    name: "alrehmanansari",
    rating: 5,
    review:
      "Shahed is extremely professional expert and he was spot on with everything. Really helped a lot to understand the whole end to end solution. Highly Recommended and i will refer you more clients. Excellent experience working with you.",
  },
  {
    name: "jeremylyas",
    rating: 5,
    review:
      "I'd certainly call on Shahed again if needed; his attention to detail, his patience, and his expertise were just what I needed in deploying my site. Highly recommend.",
  },
  {
    name: "uxpercept",
    rating: 5,
    review:
      "He works seamlessly with the team, communicates clearly, and always approaches challenges with positivity, solving problems with a smile on his face.",
  },
  {
    name: "chase_dallmann",
    rating: 5,
    review:
      "Great service helped me get my entire web application up and running after hours of struggles.",
  },
  {
    name: "ricks1014",
    rating: 5,
    review:
      "Very professional and talented young man that works on our website to make it come alive. Appreciate it and will come back for more.",
  },
  {
    name: "chris231188",
    rating: 5,
    review:
      "Fantastic work helped me fast and everything got set up as promised 10/10.",
  },
  {
    name: "boardfetish",
    rating: 5,
    review:
      "Very knowledgeable about my project and different ways to make things flow seamlessly for me. It was a great pleasure to work with him, I also learned a couple of things along the way.",
  },
  {
    name: "basitneymar",
    rating: 5,
    review:
      "It was nice working with you. He did the project on time. Well communicated.",
  },
  {
    name: "sandeeproy876",
    rating: 5,
    review: "Happy with the result, was very efficient and will work again.",
  },
  {
    name: "jeremylyas",
    rating: 5,
    review:
      "Highly recommend his services, you will not be disappointed.",
  },
  {
    name: "boardfetish",
    rating: 5,
    review:
      "He was able to tackle the task with efficiency and a great level of knowledge.",
  },
  {
    name: "uxpercept",
    rating: 5,
    review: "Always great working with him, solves problems with a smile.",
  },
  {
    name: "uxpercept",
    rating: 5,
    review: "Always helpful — this is our second job with him.",
  },
  {
    name: "claire1500",
    rating: 5,
    review:
      "We have worked together on many projects with exceptional results. Thank you.",
  },
  {
    name: "zabar0",
    rating: 5,
    review: "Shahed is my go-to guy. Great job as usual.",
  },
  {
    name: "lahaezeja",
    rating: 5,
    review: "Shahed has been of immense help.",
  },
  {
    name: "buhissopro",
    rating: 4,
    review: "Fast delivery, would work with again.",
  },
];

const Stars = ({ rating }) => (
  <div className="flex gap-0.5 text-amber-400" aria-label={`${rating} out of 5 stars`}>
    {Array.from({ length: 5 }).map((_, i) =>
      i < rating ? (
        <FaStar key={i} className="h-3.5 w-3.5" />
      ) : (
        <FaRegStar key={i} className="h-3.5 w-3.5 text-faint" />
      )
    )}
  </div>
);

const Testimonials = () => {
  const [showAll, setShowAll] = useState(false);

  const { average, total } = useMemo(() => {
    const sum = reviews.reduce((acc, r) => acc + r.rating, 0);
    return { average: (sum / reviews.length).toFixed(2), total: reviews.length };
  }, []);

  const shown = showAll ? reviews : reviews.slice(0, 6);

  return (
    <section id="testimonials" className="relative px-6 py-28 md:py-36">
      <div className="container mx-auto max-w-6xl">
        <SectionHeading
          eyebrow="Testimonials"
          title="Kind Words From Clients"
          subtitle="Real reviews from clients I've worked with on Fiverr."
        />

        {/* rating summary */}
        <Reveal direction="up">
          <div className="mb-12 flex flex-wrap items-center justify-center gap-4">
            <span className="inline-flex items-center gap-2 rounded-full border border-hairline bg-fill px-4 py-2 text-sm text-content">
              <SiFiverr className="text-lg text-emerald-400" />
              Verified Fiverr reviews
            </span>
            <span className="inline-flex items-center gap-2 rounded-full border border-amber-400/20 bg-amber-400/10 px-4 py-2 text-sm font-semibold text-amber-300">
              <FaStar className="h-3.5 w-3.5" />
              {average} average · {total} reviews
            </span>
          </div>
        </Reveal>

        {/* masonry wall */}
        <div className="columns-1 gap-6 sm:columns-2 lg:columns-3">
          {shown.map((r, index) => (
            <Reveal
              key={`${r.name}-${index}`}
              direction="up"
              delay={(index % 3) * 0.08}
              className="mb-6 break-inside-avoid"
            >
              <figure className="glass flex flex-col rounded-3xl p-6">
                <div className="mb-4 flex items-center justify-between">
                  <Stars rating={r.rating} />
                  <FaQuoteLeft className="text-xl text-content opacity-20" />
                </div>

                <blockquote className="text-sm leading-relaxed text-muted">
                  {r.review}
                </blockquote>

                <figcaption className="mt-5 flex items-center gap-3 border-t border-hairline pt-4">
                  <span className="flex h-10 w-10 items-center justify-center rounded-full bg-gradient-to-br from-aurora-violet to-aurora-indigo text-sm font-bold uppercase text-white shadow-glow">
                    {r.name[0]}
                  </span>
                  <div>
                    <p className="text-sm font-semibold text-content">{r.name}</p>
                    <p className="text-xs text-faint">Fiverr client</p>
                  </div>
                </figcaption>
              </figure>
            </Reveal>
          ))}
        </div>

        {reviews.length > 6 && (
          <div className="mt-14 flex justify-center">
            <button
              onClick={() => setShowAll((v) => !v)}
              className="rounded-full border border-hairline bg-fill px-7 py-3 text-sm font-semibold text-content backdrop-blur-md transition-all duration-300 hover:-translate-y-0.5 hover:bg-fill-2 hover:shadow-glow"
            >
              {showAll ? "Show less" : `Show all ${reviews.length} reviews`}
            </button>
          </div>
        )}
      </div>
    </section>
  );
};

export default Testimonials;
